import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddEditPageContext } from 'features/inventory/page/contexts/AddEditPageContext'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { EditSchema } from '../../schemas/editSchema'
import { Data, mapper, UPDATE_PAGE, Variables } from './api'
import { UseUpdatePage } from './types'

export const useUpdatePage = (): UseUpdatePage => {
  const { t } = useTranslation('features/page')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditPageContext)
  const [errors, setErrors] = useState<FieldErrors<EditSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_PAGE,
    onCompleted: async (data: Data) => {
      const {
        updatePage: { errors },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['getPage'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const updatePage = (data: EditSchema): void =>
    void mutateAsync({
      input: mapper(data),
    })

  return { errors, updatePage }
}
